import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";

const TEAM = [
  {
    name: "Marcus Johnson",
    role: "Owner & ISA Certified Arborist",
    bio: "15+ years in professional tree care. Founded Diversified Tree Services to bring big-company equipment to local, personalized service.",
    img: "https://public.readdy.ai/ai/img_res/116c0fd47a2a1c6b87973a7fffdf1e43.jpg",
  },
  {
    name: "Derek Williams",
    role: "Lead Climber & Crew Chief",
    bio: "Certified tree climber with 10 years experience handling complex removals. Derek's precision rigging keeps every job safe and efficient.",
    img: "https://readdy.ai/api/search-image?query=professional%20tree%20climber%20arborist%20man%20safety%20harness%20helmet%20ropes%20climbing%20gear%20smiling%20portrait%20outdoor%20trees%20background&width=300&height=300&seq=team002&orientation=squarish",
  },
  {
    name: "Tamara Brooks",
    role: "Operations Manager",
    bio: "Tamara keeps the team running on schedule, ensures every customer gets clear communication, and manages our equipment fleet.",
    img: "https://readdy.ai/api/search-image?query=professional%20woman%20operations%20manager%20tree%20service%20company%20smiling%20outdoor%20portrait%20confident%20casual%20professional%20attire&width=300&height=300&seq=team003&orientation=squarish",
  },
];

const VALUES = [
  { icon: "ri-shield-check-fill", title: "Safety First", desc: "OSHA-compliant procedures on every single job. Our crew and your property are always protected." },
  { icon: "ri-handshake-fill", title: "Honest & Transparent", desc: "We give you straight answers, written estimates, and never pressure you into unnecessary work." },
  { icon: "ri-leaf-fill", title: "Environmental Stewardship", desc: "We recycle wood chips as mulch, dispose responsibly, and respect the natural ecosystems we work in." },
  { icon: "ri-star-fill", title: "Excellence in Every Job", desc: "From a simple stump grind to a complex crane removal — we bring the same high standard to every project." },
];

export default function AboutPage() {
  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative py-24 bg-[#1a3a2e] overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20tree%20service%20crew%20team%20members%20working%20together%20in%20forest%20trees%20vibrant%20green%20foliage%20arborist%20equipment%20northern%20virginia%20outdoor&width=1440&height=500&seq=about001&orientation=landscape"
              alt="Diversified Tree Services team"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 lg:px-8 text-center">
            <span className="text-[#e8632a] font-bold text-sm uppercase tracking-widest block mb-3">About Us</span>
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-4">
              Northern Virginia's Trusted Tree Service Team
            </h1>
            <p className="text-white/70 text-xl max-w-2xl mx-auto">
              Family-owned, community-focused, and built on a foundation of honest work and real expertise since 2008.
            </p>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#e8632a] font-bold text-sm uppercase tracking-widest block mb-3">Our Story</span>
              <h2 className="text-4xl font-black text-[#1e1e1e] mb-6">From One Truck to a Full Fleet</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Diversified Tree Services LLC was founded in 2008 by Marcus Johnson, an ISA Certified Arborist who wanted to bring professional-grade tree care to homeowners across Northern Virginia without the big-corporate price tag.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                What started as one man, one truck, and a chainsaw has grown into a full-service tree company with a dedicated crew, professional equipment including bucket trucks and stump grinders, and hundreds of satisfied customers from Fredericksburg to Manassas.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                We're proud to be locally owned, operated, and invested in the communities we serve. When you call Diversified Tree Services, you're calling your neighbors — not a call center.
              </p>
              <div className="flex gap-8">
                {[["2008", "Founded"], ["2,400+", "Jobs Done"], ["200+", "5-Star Reviews"]].map(([n, l]) => (
                  <div key={l}>
                    <div className="text-3xl font-black text-[#e8632a]">{n}</div>
                    <div className="text-gray-500 text-sm font-medium">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=tree%20service%20owner%20arborist%20standing%20proudly%20next%20to%20company%20truck%20bucket%20truck%20chipper%20professional%20equipment%20northern%20virginia%20sunny%20day%20residential%20street&width=700&height=500&seq=about002&orientation=landscape"
                alt="Diversified Tree Services founder and team"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-[#f8f6f3]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#e8632a] font-bold text-sm uppercase tracking-widest block mb-3">What We Stand For</span>
              <h2 className="text-4xl font-black text-[#1e1e1e]">Our Core Values</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {VALUES.map((v) => (
                <div key={v.title} className="bg-white rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#e8632a]/10 rounded-xl mx-auto mb-4">
                    <i className={`${v.icon} text-[#e8632a] text-2xl`}></i>
                  </div>
                  <h3 className="font-black text-[#1e1e1e] text-base mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#e8632a] font-bold text-sm uppercase tracking-widest block mb-3">Meet the Crew</span>
              <h2 className="text-4xl font-black text-[#1e1e1e]">The People Behind the Work</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {TEAM.map((m) => (
                <div key={m.name} className="bg-[#f8f6f3] rounded-2xl overflow-hidden">
                  <div className="h-64 overflow-hidden">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-black text-[#1e1e1e] text-lg mb-1">{m.name}</h3>
                    <p className="text-[#e8632a] text-sm font-bold mb-3">{m.role}</p>
                    <p className="text-gray-500 text-sm leading-relaxed">{m.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#e8632a] text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-4xl font-black text-white mb-4">Work With a Team You Can Trust</h2>
            <p className="text-white/80 mb-8">Call or request a free estimate today. No pressure, no obligation.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="px-8 py-4 bg-white text-[#e8632a] font-black rounded-lg uppercase tracking-wide hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap">
                Request Free Estimate
              </Link>
              <a href="tel:+15407764488" className="px-8 py-4 border-2 border-white text-white font-black rounded-lg uppercase tracking-wide hover:bg-white hover:text-[#e8632a] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-phone-fill"></i>(540) 776-4488
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
